import { DEFAULT_BILL_TO_ADDRESS_ACCOUNTS_RECEIVABLE_ID } from 'contacts/constants';
import { ContactGroupType } from 'contacts/enums';
import { IContactGroupAddress } from 'contacts/types';

import { IAddressValue, IMergeLocationsForm, TDisabledFields } from './types';

export const getDefaultValues = (
  locations: IContactGroupAddress[],
  contactGroupTypes: ContactGroupType[],
  contactGroupType: ContactGroupType
): IMergeLocationsForm => {
  if (contactGroupType === ContactGroupType.CUSTOMER) {
    return getDefaultValuesForCustomer(
      locations,
      contactGroupTypes.includes(ContactGroupType.VENDOR)
    );
  }

  if (contactGroupType === ContactGroupType.VENDOR) {
    return getDefaultValuesForVendor(
      locations,
      contactGroupTypes.includes(ContactGroupType.CUSTOMER)
    );
  }

  return null;
};

export const getDisabledFields = (
  locations: IContactGroupAddress[],
  contactGroupTypes: ContactGroupType[],
  contactGroupType: ContactGroupType
): TDisabledFields => {
  if (contactGroupType === ContactGroupType.CUSTOMER) {
    return getDisabledFieldsForCustomer(
      locations,
      contactGroupTypes.includes(ContactGroupType.VENDOR)
    );
  }

  if (contactGroupType === ContactGroupType.VENDOR) {
    return getDisabledFieldsForVendor(
      locations,
      contactGroupTypes.includes(ContactGroupType.CUSTOMER)
    );
  }

  return {};
};

export const getAddressValueFromLocation = (location: IContactGroupAddress): IAddressValue => ({
  city: location.city,
  line1: location.line1,
  line2: location.line2,
  state: location.stateAbbreviation,
  zip: location.zip,
  country: location.countryAbbreviation,
});

const getDefaultValuesForCustomer = (
  locations: IContactGroupAddress[],
  isCustomerAndVendorType: boolean
): IMergeLocationsForm => {
  const locationsWithARNumber = locations.filter(
    (location) =>
      !!location.accountsReceivableId &&
      location.accountsReceivableId !== DEFAULT_BILL_TO_ADDRESS_ACCOUNTS_RECEIVABLE_ID
  );

  const isShipTo = locations.some((location) => location.isShipTo) || false;

  if (locationsWithARNumber.length === 1) {
    return {
      name: {
        id: locationsWithARNumber[0].addressId,
        value: locationsWithARNumber[0].name,
      },
      address: {
        id: locationsWithARNumber[0].addressId,
        value: getAddressValueFromLocation(locationsWithARNumber[0]),
      },
      externalCustomerNumber: {
        id: locationsWithARNumber[0].addressId,
        value: locationsWithARNumber[0].externalCustomerNumber,
      },
      accountsReceivableId: {
        id: locationsWithARNumber[0].addressId,
        value: locationsWithARNumber[0].accountsReceivableId,
      },
      ...(isCustomerAndVendorType && {
        accountsPayableId: {
          id: locationsWithARNumber[0].addressId,
          value: locationsWithARNumber[0].accountsPayableId,
        },
      }),
      isShipTo,
      isBillTo: locationsWithARNumber[0].isBillTo,
    };
  }

  const defaultIsBillTo = locations.some((location) => location.isBillTo) || false;

  return {
    name: null,
    address: null,
    externalCustomerNumber: null,
    accountsReceivableId: null,
    ...(isCustomerAndVendorType && {
      accountsPayableId: null,
    }),
    isShipTo,
    isBillTo: defaultIsBillTo,
  };
};

const getDefaultValuesForVendor = (
  locations: IContactGroupAddress[],
  isCustomerAndVendorType: boolean
): IMergeLocationsForm => {
  const locationsWithAPNumber = locations.filter((location) => !!location.accountsPayableId);

  if (locationsWithAPNumber.length === 1) {
    return {
      name: {
        id: locationsWithAPNumber[0].addressId,
        value: locationsWithAPNumber[0].name,
      },
      address: {
        id: locationsWithAPNumber[0].addressId,
        value: getAddressValueFromLocation(locationsWithAPNumber[0]),
      },
      accountsPayableId: {
        id: locationsWithAPNumber[0].addressId,
        value: locationsWithAPNumber[0].accountsPayableId,
      },
      ...(isCustomerAndVendorType && {
        externalCustomerNumber: {
          id: locationsWithAPNumber[0].addressId,
          value: locationsWithAPNumber[0].externalCustomerNumber,
        },
        accountsReceivableId: {
          id: locationsWithAPNumber[0].addressId,
          value: locationsWithAPNumber[0].accountsReceivableId,
        },
      }),
    };
  }

  return {
    name: null,
    address: null,
    accountsPayableId: null,
    ...(isCustomerAndVendorType && {
      externalCustomerNumber: null,
      accountsReceivableId: null,
    }),
  };
};

const getDisabledFieldsForCustomer = (
  locations: IContactGroupAddress[],
  isCustomerAndVendorType: boolean
): TDisabledFields => {
  const locationsWithARNumber = locations.filter(
    (location) =>
      !!location.accountsReceivableId &&
      location.accountsReceivableId !== DEFAULT_BILL_TO_ADDRESS_ACCOUNTS_RECEIVABLE_ID
  );

  if (locationsWithARNumber.length === 1) {
    return {
      name: true,
      address: !!locationsWithARNumber[0]?.country && !!locationsWithARNumber[0]?.zip,
      externalCustomerNumber: !!locationsWithARNumber[0]?.externalCustomerNumber,
      accountsReceivableId: true,
      ...(isCustomerAndVendorType && {
        accountsPayableId: !!locationsWithARNumber[0]?.accountsPayableId,
      }),
      isShipTo: true,
      isBillTo: true,
    };
  }

  return {};
};

const getDisabledFieldsForVendor = (
  locations: IContactGroupAddress[],
  isCustomerAndVendorType: boolean
): TDisabledFields => {
  const locationsWithAPNumber = locations.filter((location) => !!location.accountsPayableId);

  if (locationsWithAPNumber.length === 1) {
    return {
      name: true,
      address: !!locationsWithAPNumber[0]?.country && !!locationsWithAPNumber[0]?.zip,
      accountsPayableId: true,
      ...(isCustomerAndVendorType && {
        externalCustomerNumber: !!locationsWithAPNumber[0]?.externalCustomerNumber,
        accountsReceivableId: !!locationsWithAPNumber[0]?.accountsReceivableId,
      }),
    };
  }

  return {};
};
